// react
import React, { useState, useCallback, useMemo } from 'react';

// components
import { Icon } from '@/components/atoms/Icon/Icon';
import { SquiggleCard } from '@/pwa/atoms/SquiggleCard/SquiggleCard';

// mui
import {
  Box,
  Chip,
  Avatar,
  Stack,
  MenuItem,
  MenuList,
  Menu,
  ListItemIcon,
  Drawer,
  Typography,
  IconButton,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// models
import { CareCircle, CareCircleMember } from '@karehero/models';

const menuSlotProps = {
  paper: {
    variant: 'outlined' as const,
    elevation: 0,
    sx: {
      mt: 1.5,
    },
  },
};

type AccountInfoOrder = 'avatar-first' | 'avatar-last';

export interface AccountInfoProps {
  role: string;
  name: string;
  fallback: string;
  onSignOut?: () => void;
  toAccountSettings?: string;
  avatarSrc?: string;
  careCircles?: CareCircle[];
  onCareCircleChange?: (careCircleId: string) => void;
  currentCareCircleMember?: CareCircleMember;
  currentCareCircle?: CareCircle;
  onNewCareCircle?: () => void;
  navigate?: (to: string) => void;
  isMultipleCareCircles?: boolean;
  isMenuDisabled?: boolean;
  hideLabel?: boolean;
  order?: AccountInfoOrder;
  _isOpenOverride?: boolean;
}

/**
 * AccountInfo displays the account information and has a dropdown menu for
 * account settings.
 */
export const AccountInfo = ({
  currentCareCircleMember,
  careCircles,
  onCareCircleChange,
  role,
  name,
  fallback,
  onSignOut,
  toAccountSettings,
  avatarSrc,
  currentCareCircle,
  onNewCareCircle,
  navigate,
  isMultipleCareCircles = false,
  isMenuDisabled = false,
  hideLabel = false,
  order = 'avatar-last',
  _isOpenOverride,
}: AccountInfoProps) => {
  // hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // memo
  const computedAvatarSrc = useMemo(() => {
    if (avatarSrc?.includes('d=blank')) return undefined;
    return avatarSrc;
  }, [avatarSrc]);

  // state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isOpen, setIsOpen] = useState(_isOpenOverride || false);

  // methods
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (anchorEl) setIsOpen((prev) => !prev);
    },
    [anchorEl],
  );
  const handleClose = () => setIsOpen(false);

  const roleText =
    isMultipleCareCircles &&
    role === 'Member' &&
    currentCareCircleMember?.roles?.length
      ? currentCareCircleMember.roles.map((r) => r.name).join(' · ')
      : role;

  const commonItems = (
    <>
      {isMultipleCareCircles && careCircles && careCircles.length > 0 && (
        <>
          <Box
            sx={{
              paddingX: 2,
              paddingY: 1,
            }}
          >
            <Typography variant='body2'>Person I'm Supporting</Typography>
          </Box>
          {careCircles?.map((careCircle) => (
            <MenuItem
              key={careCircle.id}
              onClick={(e) => {
                e.preventDefault();
                onCareCircleChange?.(careCircle.id);
              }}
            >
              <Typography variant='body1'>
                {careCircle.careRecipientAccount.fullName || 'Your loved one'}{' '}
                {currentCareCircle?.id === careCircle.id && (
                  <Chip label='Current' />
                )}
              </Typography>
            </MenuItem>
          ))}
          <MenuItem onClick={() => onNewCareCircle?.()}>
            <ListItemIcon>
              <Icon icon='user-plus' fontSize={16} />
            </ListItemIcon>
            Add person to support
          </MenuItem>
        </>
      )}
      {toAccountSettings && (
        <MenuItem onClick={() => navigate?.(toAccountSettings)}>
          <ListItemIcon>
            <Icon icon='gear' fontSize={16} />
          </ListItemIcon>
          Settings
        </MenuItem>
      )}
      <MenuItem
        onClick={onSignOut}
        sx={{
          color: 'error.main',
          '& .MuiListItemIcon-root': {
            color: 'error.main',
          },
        }}
      >
        <ListItemIcon color='error'>
          <Icon icon='arrow-right-from-bracket' fontSize={16} color='error' />
        </ListItemIcon>
        Sign out
      </MenuItem>
    </>
  );

  const menuContent = (
    <>
      <Box
        sx={{
          paddingX: 2,
          paddingBottom: 1,
        }}
      >
        <Stack spacing={0.5}>
          <Typography variant='body2'>Signed in as {name}</Typography>
          <Stack
            direction='row'
            spacing={0.5}
            sx={{
              flexWrap: 'wrap',
            }}
          >
            {isMultipleCareCircles &&
            role === 'Member' &&
            currentCareCircleMember?.roles?.length ? (
              currentCareCircleMember.roles.map((r) => (
                <Chip key={r.name} label={r.name} size='small' />
              ))
            ) : (
              <Chip label={role} size='small' />
            )}
          </Stack>
        </Stack>
      </Box>
      {commonItems}
    </>
  );

  const drawerContent = (
    <>
      <Box sx={{ p: 2 }}>
        <SquiggleCard
          variant='compact'
          squiggleColor='lime'
          title={name}
          subtitle={roleText}
          avatar={
            <Avatar alt={name} src={computedAvatarSrc}>
              {fallback}
            </Avatar>
          }
        />
      </Box>
      {commonItems}
    </>
  );

  return (
    <>
      <Stack
        direction={order === 'avatar-first' ? 'row-reverse' : 'row'}
        spacing={1}
        sx={{
          alignItems: 'center',
        }}
      >
        {!hideLabel && (
          <Stack
            sx={{
              alignItems: order === 'avatar-first' ? 'flex-start' : 'flex-end',
            }}
          >
            <Typography
              variant='caption'
              sx={{
                whiteSpace: 'nowrap',
              }}
            >
              {isMultipleCareCircles
                ? role !== 'Member'
                  ? role
                  : currentCareCircleMember?.roles
                      .map((role) => role.name)
                      .join(', ')
                : role}
            </Typography>
            <Typography
              variant='body2'
              sx={{
                whiteSpace: 'nowrap',
              }}
            >
              {name}
            </Typography>
          </Stack>
        )}

        {/* dropdown start */}
        <Tooltip
          title='Account settings'
          sx={isMenuDisabled ? { pointerEvents: 'none' } : {}}
        >
          <IconButton
            ref={setAnchorEl}
            onClick={handleClick}
            size='small'
            aria-controls={isOpen ? 'account-menu' : undefined}
            aria-haspopup='true'
            aria-expanded={isOpen ? 'true' : undefined}
          >
            <Avatar alt={name} src={computedAvatarSrc}>
              {fallback}
            </Avatar>
          </IconButton>
        </Tooltip>
        {/* dropdown end */}
      </Stack>
      {anchorEl &&
        (isMobile ? (
          <Drawer
            anchor='bottom'
            open={isOpen}
            onClose={handleClose}
            slotProps={{
              paper: {
                sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16 },
              },
            }}
          >
            {/* MenuItems need a MenuList around them outside a Menu. */}
            <MenuList
              onClick={handleClose}
              sx={(theme) => ({
                pt: 1,
                pb: `calc(env(safe-area-inset-bottom) + ${theme.spacing(4)})`,
              })}
            >
              {drawerContent}
            </MenuList>
          </Drawer>
        ) : (
          <Menu
            anchorEl={anchorEl}
            id='account-menu'
            open={isOpen}
            onClose={handleClose}
            onClick={handleClose}
            slotProps={menuSlotProps}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {menuContent}
          </Menu>
        ))}
    </>
  );
};

AccountInfo.displayName = 'AccountInfo';
export default AccountInfo;
